// =============================================
// UTILITÁRIO: DIAGNÓSTICO DE AUTENTICAÇÃO
// =============================================
// Arquivo: authDiagnostics.ts
// Descrição: Funções para diagnosticar problemas de autenticação
// =============================================

import { Config, getToken, isTokenExpired, getUser } from '../services/config';

export interface AuthDiagnostics {
  hasToken: boolean;
  tokenValue: string | null;
  tokenPrefix: string | null;
  isExpired: boolean;
  hasUser: boolean;
  user: any;
  tokenKey: string;
  expiryKey: string;
  allLocalStorageKeys: string[];
  recommendation: string;
}

/**
 * Executa diagnóstico completo da autenticação
 */
export function diagnoseAuth(): AuthDiagnostics {
  const token = getToken();
  const user = getUser();
  const expired = isTokenExpired();

  // Obter todas as chaves do localStorage
  const allKeys = Object.keys(localStorage);

  // Tentar extrair primeiros 20 caracteres do token
  const tokenPrefix = token ? token.substring(0, 20) : null;

  // Determinar recomendação
  let recommendation = '';

  if (!token) {
    recommendation = '❌ Token não encontrado. Faça login novamente.';
  } else if (expired) {
    recommendation = '⏰ Token expirado. Faça login novamente.';
  } else if (!user) {
    recommendation = '👤 Usuário não encontrado. Faça login novamente.';
  } else {
    recommendation = '✅ Autenticação OK!';
  }

  return {
    hasToken: !!token,
    tokenValue: token,
    tokenPrefix,
    isExpired: expired,
    hasUser: !!user,
    user,
    tokenKey: Config.AUTH.TOKEN_KEY,
    expiryKey: Config.AUTH.TOKEN_EXPIRY_KEY,
    allLocalStorageKeys: allKeys,
    recommendation
  };
}

/**
 * Imprime diagnóstico no console
 */
export function printAuthDiagnostics(): void {
  const diag = diagnoseAuth();

  console.log('\n🔍 ========================================');
  console.log('🔍 DIAGNÓSTICO DE AUTENTICAÇÃO');
  console.log('🔍 ========================================');
  console.log(`Token Key: ${diag.tokenKey}`);
  console.log(`Expiry Key: ${diag.expiryKey}`);
  console.log(`\nHas Token: ${diag.hasToken ? '✅ Sim' : '❌ Não'}`);

  if (diag.hasToken) {
    console.log(`Token Prefix: ${diag.tokenPrefix}...`);
    console.log(`Token Length: ${diag.tokenValue?.length || 0} caracteres`);
    console.log(`Is Expired: ${diag.isExpired ? '⏰ Sim' : '✅ Não'}`);
  }

  console.log(`\nHas User: ${diag.hasUser ? '✅ Sim' : '❌ Não'}`);

  if (diag.hasUser) {
    console.log('User Data:', diag.user);
  }

  console.log(`\nLocalStorage Keys (${diag.allLocalStorageKeys.length}):`);
  diag.allLocalStorageKeys.forEach(key => {
    const value = localStorage.getItem(key);
    const preview = value && value.length > 50 ? value.substring(0, 50) + '...' : value;
    console.log(`  - ${key}: ${preview}`);
  });

  console.log(`\n${diag.recommendation}`);
  console.log('🔍 ========================================\n');
}

/**
 * Limpa completamente a autenticação
 */
export function clearAuth(): void {
  console.log('🗑️  Limpando autenticação...');
  localStorage.removeItem(Config.AUTH.TOKEN_KEY);
  localStorage.removeItem(Config.AUTH.TOKEN_EXPIRY_KEY);
  localStorage.removeItem(Config.AUTH.USER_KEY);
  console.log('✅ Autenticação limpa. Faça login novamente.');
}

/**
 * Força um novo token (útil para testes)
 */
export function setDebugToken(token: string): void {
  console.warn('⚠️  Definindo token de debug. Use apenas para testes!');
  localStorage.setItem(Config.AUTH.TOKEN_KEY, token);

  const expiryDate = new Date();
  expiryDate.setHours(expiryDate.getHours() + 24);
  localStorage.setItem(Config.AUTH.TOKEN_EXPIRY_KEY, expiryDate.toISOString());

  console.log('✅ Token de debug definido.');
  printAuthDiagnostics();
}

// Exportar para uso global via console
if (typeof window !== 'undefined') {
  (window as any).authDiag = {
    diagnose: diagnoseAuth,
    print: printAuthDiagnostics,
    clear: clearAuth,
    setDebugToken
  };

  console.log('💡 Funções de diagnóstico disponíveis:');
  console.log('   window.authDiag.print()      - Imprimir diagnóstico');
  console.log('   window.authDiag.diagnose()   - Retornar objeto de diagnóstico');
  console.log('   window.authDiag.clear()      - Limpar autenticação');
}
